import {
  DndContext,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToFirstScrollableAncestor } from '@dnd-kit/modifiers';
import { rectSortingStrategy, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import type { ColumnOrderState, ColumnPinningState, VisibilityState } from '@tanstack/react-table';
import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { cn } from '../../styles/lib/utils';
import { Button, IconButton } from '../Button';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter } from '../Dialog';
import { closestTopCenterCollisionDetection, Draggable, DragHandle, DragOverlay } from '../Drag';
import { Icon } from '../Icon';
import { Label } from '../Label';
import { Separator } from '../Separator';
import { Switch } from '../Switch';
import type { TableColumn } from '../Table';
import { Text } from '../Text';
import { Tooltip } from '../Tooltip';

function filterSelectFromPinning(pinning: ColumnPinningState): ColumnPinningState {
  return {
    left: pinning.left?.filter((id) => id !== 'select') || [],
    right: pinning.right || [],
  };
}

// Helper to filter out 'select' from column order
function filterSelectFromOrder(order: ColumnOrderState): ColumnOrderState {
  return order.filter((id) => id !== 'select');
}

// Helper to filter out 'select' from columns
function filterSelectFromColumns<TData>(columns: TableColumn<TData>[]): string[] {
  return columns.filter((col) => col.id !== 'select').map((col) => col.id);
}

function columnHeaderText<TData>(column: TableColumn<TData>): string {
  return typeof column.header === 'string' ? column.header : column.id || '';
}

function reorderLabel(headerText: string): string {
  return headerText ? `Reorder ${headerText}` : 'Reorder column';
}

type MoveDirection = 'up' | 'down';

type PinningSection = 'left' | 'center' | 'right';

const SECTION_NAMES: Record<PinningSection, string | null> = { left: 'Left Pins', center: null, right: 'Right Pins' };

function moveLabel(headerText: string, direction: MoveDirection): string {
  return `Move ${headerText || 'column'} ${direction}`;
}

function EndDropzone({
  section,
  activeId,
  overId,
  className,
}: {
  section: 'left' | 'center' | 'right';
  activeId: string | null;
  overId: string | null;
  className?: string;
}) {
  const dropzoneId = `${section}-end-dropzone`;
  const { setNodeRef, isOver } = useDroppable({
    id: dropzoneId,
  });

  const isActive = activeId !== null && (overId === dropzoneId || isOver);

  return (
    <div
      ref={setNodeRef}
      className={cn('my-1 h-1 w-full rounded-full transition-colors', isActive ? 'bg-blue-40' : '', className)}
    />
  );
}

function ColumnItem<TData>({
  column,
  dragHandleProps,
  dragHandleClassName,
  localVisibility,
  handleSwitch,
  canHide,
  canReorder,
  isDragging = false,
  showDropIndicator = false,
  showMoveButtons = false,
  canMoveUp = false,
  canMoveDown = false,
  onMove,
  registerMoveButton,
}: {
  column: TableColumn<TData>;
  dragHandleProps?: any;
  dragHandleClassName?: string;
  localVisibility: VisibilityState;
  handleSwitch: (columnId: string, checked: boolean) => void;
  canHide: boolean;
  canReorder: boolean;
  isDragging?: boolean;
  showDropIndicator?: boolean;
  showMoveButtons?: boolean;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
  onMove?: (columnId: string, direction: MoveDirection) => void;
  registerMoveButton?: (columnId: string, direction: MoveDirection, button: HTMLButtonElement | null) => void;
}) {
  const columnId = column.id || '';
  const switchId = useId();
  const isVisible = localVisibility[columnId] !== false;
  const headerText = columnHeaderText(column);
  return (
    <div
      className={cn(
        'relative flex h-5 items-center justify-between px-1.5',
        showMoveButtons ? 'w-[264px]' : 'w-[220px]',
      )}
    >
      {/* Drop indicator */}
      <div
        className={cn(
          'pointer-events-none absolute left-0 top-[-4px] z-10 h-1 w-full rounded-full transition-colors',
          showDropIndicator ? 'bg-blue-40' : 'bg-transparent',
        )}
      />
      <div className="flex min-w-0 flex-1 items-center gap-1">
        {canReorder && (
          <>
            <DragHandle
              {...dragHandleProps}
              aria-label={reorderLabel(headerText)}
              variant="default"
              size="icon"
              className={cn(isDragging ? 'hover:bg-white' : '', dragHandleClassName)}
            >
              <Icon icon="dragHandleDots2" size={16} />
            </DragHandle>
            {showMoveButtons && (
              <div className="flex shrink-0 items-center">
                <IconButton
                  ref={(button) => registerMoveButton?.(columnId, 'up', button)}
                  icon="arrowUp"
                  iconSize={16}
                  size="icon"
                  variant="hollow"
                  aria-label={moveLabel(headerText, 'up')}
                  disabled={!canMoveUp}
                  onClick={() => onMove?.(columnId, 'up')}
                  data-testid={`manage-columns-move-up-${columnId}`}
                />
                <IconButton
                  ref={(button) => registerMoveButton?.(columnId, 'down', button)}
                  icon="arrowDown"
                  iconSize={16}
                  size="icon"
                  variant="hollow"
                  aria-label={moveLabel(headerText, 'down')}
                  disabled={!canMoveDown}
                  onClick={() => onMove?.(columnId, 'down')}
                  data-testid={`manage-columns-move-down-${columnId}`}
                />
              </div>
            )}
          </>
        )}
        <Label
          htmlFor={switchId}
          className={cn('min-w-0', canHide && column.enableHiding !== false && 'cursor-pointer')}
          data-testid={`manage-columns-label-${columnId}`}
        >
          <Text className="text-primary-dark truncate text-xs">{String(headerText)}</Text>
        </Label>
      </div>
      <div className="flex items-center gap-1">
        {column.enableHiding === false && (
          <Tooltip
            text="This column cannot be hidden because it is the primary record on this table."
            side="top"
            delayDuration={50}
            className="max-w-[200px] text-center"
            aria-label="Why this column is always shown"
            triggerClassName="inline-flex shrink-0 cursor-default rounded-[6px] border border-transparent p-1"
            data-testid={`manage-columns-locked-info-${columnId}`}
          >
            <Icon icon="infoCircled" size={14} className="text-primary-dark" />
          </Tooltip>
        )}
        <Switch
          id={switchId}
          checked={isVisible}
          onCheckedChange={(checked) => handleSwitch(columnId, checked)}
          disabled={!canHide || column.enableHiding === false}
          data-testid={`manage-columns-switch-${columnId}`}
        />
      </div>
    </div>
  );
}

export type ManageColumnsDialogProps<TData> = {
  columns: TableColumn<TData>[];
  columnVisibility: VisibilityState;
  onColumnVisibilityChange: (visibility: VisibilityState) => void;
  columnOrder?: ColumnOrderState;
  onColumnOrderChange?: (order: ColumnOrderState) => void;
  columnPinning?: ColumnPinningState;
  onColumnPinningChange?: (pinning: ColumnPinningState) => void;
  /** Let users show and hide columns. Defaults to true. */
  enableHiding?: boolean;
  /** Let users drag columns between pinned groups. Defaults to true. */
  enableColumnPinning?: boolean;
  /** Let users drag columns to reorder them. Defaults to true. */
  enableColumnOrdering?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

function ManageColumnsDialog<TData>({
  columns,
  columnVisibility,
  onColumnVisibilityChange,
  columnOrder,
  onColumnOrderChange,
  columnPinning,
  onColumnPinningChange,
  enableHiding = true,
  enableColumnPinning = true,
  enableColumnOrdering = true,
  open,
  onOpenChange,
}: ManageColumnsDialogProps<TData>) {
  const [localVisibility, setLocalVisibility] = useState<VisibilityState>(columnVisibility);

  const [localColumnOrder, setLocalColumnOrder] = useState<ColumnOrderState>(() => {
    // Initialize with provided order (filtering out 'select') or create from columns
    if (columnOrder) {
      return filterSelectFromOrder(columnOrder);
    }
    return filterSelectFromColumns(columns);
  });
  const [localColumnPinning, setLocalColumnPinning] = useState<ColumnPinningState>(
    filterSelectFromPinning(columnPinning ?? { left: [], right: [] }),
  );
  const [moveAnnouncement, setMoveAnnouncement] = useState('');
  const [showMoveButtons, setShowMoveButtons] = useState(false);
  const showMoveButtonsId = useId();

  // Update local state when props change and dialog is open
  useEffect(() => {
    if (open) {
      setMoveAnnouncement('');
      setShowMoveButtons(false);
      setLocalVisibility(columnVisibility);
      if (columnOrder) {
        setLocalColumnOrder(filterSelectFromOrder(columnOrder));
      } else {
        // If no order provided, use default column order
        setLocalColumnOrder(filterSelectFromColumns(columns));
      }
      setLocalColumnPinning(filterSelectFromPinning(columnPinning ?? { left: [], right: [] }));
    }
  }, [columnVisibility, columnOrder, columnPinning, open, columns]);

  // Get visual column sections (left pinned, center, right pinned)
  const columnPinningSections = useMemo(() => {
    const leftColumns = localColumnPinning?.left ?? [];
    const rightColumns = localColumnPinning?.right ?? [];
    const centerColumns = localColumnOrder.filter((id) => !leftColumns.includes(id) && !rightColumns.includes(id));

    return {
      left: leftColumns,
      center: centerColumns,
      right: rightColumns,
    };
  }, [localColumnPinning, localColumnOrder]);

  // Helper function to determine which section a column belongs to
  const getSection = useCallback(
    (id: string): 'left' | 'center' | 'right' | null => {
      // Handle dropzone IDs
      if (id.endsWith('-end-dropzone')) {
        const section = id.replace('-end-dropzone', '') as 'left' | 'center' | 'right';
        if (section === 'left' || section === 'center' || section === 'right') {
          return section;
        }
      }
      if (columnPinningSections.left.includes(id)) return 'left';
      if (columnPinningSections.right.includes(id)) return 'right';
      if (columnPinningSections.center.includes(id)) return 'center';
      return null;
    },
    [columnPinningSections],
  );

  // Create a map of column ID to column for quick lookup
  const columnMap = useMemo(() => {
    const map = new Map<string, TableColumn<TData>>();
    columns
      .filter((col) => col.id !== 'select')
      .forEach((col) => {
        if (col.id) {
          map.set(col.id, col);
        }
      });
    return map;
  }, [columns]);

  // Order columns based on localColumnOrder, then append any missing columns
  const orderedColumnsPinningSections = useMemo(() => {
    const ordered: { left: TableColumn<TData>[]; center: TableColumn<TData>[]; right: TableColumn<TData>[] } = {
      left: [],
      center: [],
      right: [],
    };

    // Add columns in the order specified by localColumnOrder
    columnPinningSections.left.forEach((id) => {
      const col = columnMap.get(id);
      if (col) {
        ordered.left.push(col);
      }
    });
    columnPinningSections.center.forEach((id) => {
      const col = columnMap.get(id);
      if (col) {
        ordered.center.push(col);
      }
    });
    columnPinningSections.right.forEach((id) => {
      const col = columnMap.get(id);
      if (col) {
        ordered.right.push(col);
      }
    });
    return ordered;
  }, [columnPinningSections, columnMap]);

  // Group ordered columns (in center section) based on count: max 3 groups, min 8 items per group
  const centerPinningSectionColumnGroups = useMemo(() => {
    const orderedColumns = orderedColumnsPinningSections.center;
    const total = orderedColumns.length;

    let rowLength = 8;
    if (orderedColumnsPinningSections.left.length > 0 || orderedColumnsPinningSections.right.length > 0) {
      rowLength = 14;
    }

    if (total <= rowLength) {
      // Single group if rowLength or fewer columns
      return [orderedColumns];
    } else if (total <= rowLength * 2) {
      // Two groups: distribute evenly
      const perGroup = Math.ceil(total / 2);
      const firstGroup = orderedColumns.slice(0, perGroup);
      const secondGroup = orderedColumns.slice(perGroup);
      return [firstGroup, secondGroup];
    } else {
      // Three groups: distribute evenly
      const perGroup = Math.ceil(total / 3);
      const firstGroup = orderedColumns.slice(0, perGroup);
      const remaining = total - perGroup;
      const secondGroupSize = Math.ceil(remaining / 2);
      const secondGroup = orderedColumns.slice(perGroup, perGroup + secondGroupSize);
      const thirdGroup = orderedColumns.slice(perGroup + secondGroupSize);
      return [firstGroup, secondGroup, thirdGroup];
    }
  }, [orderedColumnsPinningSections]);

  // Track active drag ID for DragOverlay
  const [activeId, setActiveId] = useState<string | null>(null);
  // Track over ID to show drop indicator
  const [overId, setOverId] = useState<string | null>(null);

  // Get sortable items for each section (excluding select and active, like TableHeader/TableBody)
  // The active column will still be rendered but not in SortableContext, so it stays in place with 50% opacity
  const leftSortableItems = useMemo(() => {
    const sectionColumnIds = orderedColumnsPinningSections.left.map((col) => col.id);
    return sectionColumnIds.filter((id) => id !== activeId);
  }, [orderedColumnsPinningSections.left, activeId]);

  const centerSortableItems = useMemo(() => {
    const sectionColumnIds = orderedColumnsPinningSections.center.map((col) => col.id);
    return sectionColumnIds.filter((id) => id !== activeId);
  }, [orderedColumnsPinningSections.center, activeId]);

  const rightSortableItems = useMemo(() => {
    const sectionColumnIds = orderedColumnsPinningSections.right.map((col) => col.id);
    return sectionColumnIds.filter((id) => id !== activeId);
  }, [orderedColumnsPinningSections.right, activeId]);

  const handleSwitch = (columnId: string, checked: boolean) => {
    setLocalVisibility((prev) => ({
      ...prev,
      [columnId]: checked,
    }));
  };

  // Setup DnD sensors
  const sensors = useSensors(
    // Mouse sensor with a distance constraint of 5 pixels
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 5, // Drag starts after moving 5 pixels
      },
    }),
    // Touch sensor with a delay and tolerance constraint
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200, // User must hold for 200ms
        tolerance: 5, // Allows for 5 pixels of movement during the delay
      },
    }),
    useSensor(KeyboardSensor, {}),
  );

  // Handle drag start to set activeId
  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string);
    setOverId(null);
  }, []);

  // Handle drag over to track drop position
  const handleDragOver = useCallback((event: DragOverEvent) => {
    const { active, over } = event;
    if (!active || !over) {
      setOverId(null);
      return;
    }

    const overId = over.id as string;
    setOverId(overId);
  }, []);

  // Handle drag end to reorder columns and update pinning
  // Get the active and over sections and remove the active column from the source section
  // Insert the active column into the target section at the correct position
  // Update the pinning state to reflect the move
  // Update the column order
  const reorderColumns = useCallback(
    (activeId: string, overId: string) => {
      // Handle regular column drops
      const activeSection = getSection(activeId);
      const overSection = getSection(overId);

      if (!activeSection || !overSection) return;
      if (!enableColumnPinning && activeSection !== overSection) return;

      // Get section arrays
      const leftIds = columnPinningSections.left;
      const centerIds = columnPinningSections.center;
      const rightIds = columnPinningSections.right;

      // Check if dropping on an end dropzone
      const isEndDropzone = overId.endsWith('-end-dropzone');

      // Get the target section array (where we're dropping)
      const targetSectionArray = overSection === 'left' ? leftIds : overSection === 'right' ? rightIds : centerIds;

      // Get the source section array (where we're dragging from)
      const sourceSectionArray = activeSection === 'left' ? leftIds : activeSection === 'right' ? rightIds : centerIds;

      // Remove active column from source section
      const sourceWithoutActive = sourceSectionArray.filter((id) => id !== activeId);

      // Insert active column into target section at the correct position
      const targetWithoutActive = targetSectionArray.filter((id) => id !== activeId);
      let insertIndex: number;

      if (isEndDropzone) {
        // Insert at the end
        insertIndex = targetWithoutActive.length;
      } else {
        // Insert before the overId item
        insertIndex = targetWithoutActive.indexOf(overId);
        if (insertIndex === -1) return;
      }

      const reorderedTarget = [
        ...targetWithoutActive.slice(0, insertIndex),
        activeId,
        ...targetWithoutActive.slice(insertIndex),
      ];

      // Update pinning state to reflect the move
      setLocalColumnPinning((prev) => {
        const newPinning = { ...prev };
        // Remove from source section
        if (activeSection === 'left' || activeSection === 'right') {
          newPinning[activeSection] = sourceWithoutActive;
        }
        // Add to target section
        if (overSection === 'left' || overSection === 'right') {
          newPinning[overSection] = reorderedTarget;
        }
        return newPinning;
      });

      // Update column order
      setLocalColumnOrder((prev) => {
        const newOrder = [...prev];
        newOrder.splice(newOrder.indexOf(activeId), 1);
        newOrder.splice(isEndDropzone ? newOrder.length : newOrder.indexOf(overId), 0, activeId);
        return newOrder;
      });
    },
    [getSection, columnPinningSections, enableColumnPinning],
  );

  const moveButtons = useRef(new Map<string, HTMLButtonElement>());
  const pendingMoveFocus = useRef<{ columnId: string; direction: MoveDirection } | null>(null);

  const registerMoveButton = useCallback(
    (columnId: string, direction: MoveDirection, button: HTMLButtonElement | null) => {
      const key = `${direction}:${columnId}`;
      if (button) moveButtons.current.set(key, button);
      else moveButtons.current.delete(key);
    },
    [],
  );

  const moveColumn = useCallback(
    (columnId: string, direction: MoveDirection) => {
      const section = getSection(columnId);
      if (!section) return;
      // Rendered rows only: a saved order can still list a column the table no longer has.
      const ids = orderedColumnsPinningSections[section].map((column) => column.id || '');
      const index = ids.indexOf(columnId);
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      if (index === -1 || newIndex < 0 || newIndex >= ids.length) return;

      // Down swaps with the next column, so the column never lands after its section's end.
      if (direction === 'up') reorderColumns(columnId, ids[newIndex]);
      else reorderColumns(ids[newIndex], columnId);

      const column = columnMap.get(columnId);
      const sectionName = SECTION_NAMES[section];
      setMoveAnnouncement(
        `${column ? columnHeaderText(column) : columnId} moved to position ${newIndex + 1} of ${ids.length}${sectionName ? ` in ${sectionName}` : ''}`,
      );
      pendingMoveFocus.current = { columnId, direction };
    },
    [getSection, orderedColumnsPinningSections, reorderColumns, columnMap],
  );

  // A row can remount when it crosses a center column group, and a button disabled at a section end drops focus.
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs after each reorder commits, to restore focus on the moved row
  useLayoutEffect(() => {
    const pending = pendingMoveFocus.current;
    if (!pending) return;
    pendingMoveFocus.current = null;
    const pressed = moveButtons.current.get(`${pending.direction}:${pending.columnId}`);
    const other = moveButtons.current.get(`${pending.direction === 'up' ? 'down' : 'up'}:${pending.columnId}`);
    const target = pressed && !pressed.disabled ? pressed : other;
    if (target && document.activeElement !== target) target.focus();
  }, [orderedColumnsPinningSections]);

  // Handle drag end to reorder columns within the same section only
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!active || !over || active.id === over.id) return;

      const activeId = active.id as string;
      const overId = over.id as string;

      reorderColumns(activeId, overId);
    },
    [reorderColumns],
  );

  // Handle drag end to clear state (following Table.tsx pattern)
  const handleDragEndWithCleanup = useCallback(
    (event: DragEndEvent) => {
      setActiveId(null);
      setOverId(null);
      handleDragEnd(event);
    },
    [handleDragEnd],
  );

  // Get the active column for DragOverlay
  const activeColumn = useMemo(() => {
    if (!activeId) return null;
    return columns.find((col) => col.id === activeId);
  }, [activeId, columns]);

  // Save the local state to the parent state, adding 'select' to the beginning of the order if it was there originally
  const handleSave = () => {
    onColumnVisibilityChange(localVisibility);
    if (onColumnOrderChange) {
      // If original order had 'select' at the beginning, preserve it
      const hasSelectInOrder = columnOrder && columnOrder[0] === 'select';
      const newOrder = hasSelectInOrder ? ['select', ...localColumnOrder] : localColumnOrder;
      onColumnOrderChange(newOrder);
    }
    if (onColumnPinningChange) {
      const hasSelectInPinning = columnPinning?.left && columnPinning.left[0] === 'select';
      const newPinning = hasSelectInPinning
        ? { left: ['select', ...(localColumnPinning.left || [])], right: localColumnPinning.right }
        : localColumnPinning;
      onColumnPinningChange(newPinning);
    }
    onOpenChange(false);
  };

  const handleCancel = () => {
    setLocalVisibility(columnVisibility);
    if (columnOrder) {
      setLocalColumnOrder(filterSelectFromOrder(columnOrder));
    } else {
      setLocalColumnOrder(filterSelectFromColumns(columns));
    }
    setLocalColumnPinning(columnPinning ?? { left: [], right: [] });
    onOpenChange(false);
  };

  // Reset local state when dialog opens
  const handleOpenChange = (isOpen: boolean) => {
    onOpenChange(isOpen);
    if (isOpen) {
      setLocalVisibility(columnVisibility);
      if (columnOrder) {
        setLocalColumnOrder(filterSelectFromOrder(columnOrder));
      } else {
        setLocalColumnOrder(filterSelectFromColumns(columns));
      }
      setLocalColumnPinning(columnPinning ?? { left: [], right: [] });
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className={cn(activeId ? 'cursor-grabbing' : '', 'w-fit max-w-[90vw]')}
        data-testid="manage-columns-dialog"
        title="Columns"
      >
        <DialogDescription className="sr-only">
          Manage column visibility and order. Drag columns to reorder them, or turn on Show reorder buttons, and use the
          switches to show or hide columns.
        </DialogDescription>
        <output aria-live="polite" className="sr-only" data-testid="manage-columns-reorder-status">
          {moveAnnouncement}
        </output>
        <DialogBody className="px-3 py-3">
          <DndContext
            collisionDetection={closestTopCenterCollisionDetection}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEndWithCleanup}
            sensors={sensors}
            modifiers={[restrictToFirstScrollableAncestor]}
          >
            <div className="flex w-full gap-3">
              {/* Left pinned columns */}
              {orderedColumnsPinningSections.left.length > 0 && (
                <div className="border-grey-30 relative flex flex-col gap-2 rounded-lg border p-2">
                  <div className="flex items-center gap-1">
                    <Icon icon="drawingPin" size={16} className="text-grey-60" />
                    <Text className="text-2xs text-grey-60 font-semibold">Left Pins</Text>
                  </div>
                  <SortableContext items={leftSortableItems} strategy={verticalListSortingStrategy}>
                    <div className="flex flex-col">
                      {orderedColumnsPinningSections.left.map((column, index, sectionColumns) => {
                        const columnId = column.id || '';
                        const showDropIndicator = !!(activeId && overId === columnId);

                        return (
                          <Draggable
                            key={columnId}
                            id={columnId}
                            provideDragHandleProps
                            disabled={!enableColumnOrdering}
                            isDragOverlay={true}
                            className="py-1.5"
                          >
                            <ColumnItem
                              column={column}
                              localVisibility={localVisibility}
                              handleSwitch={handleSwitch}
                              canHide={enableHiding}
                              canReorder={enableColumnOrdering}
                              showMoveButtons={showMoveButtons}
                              showDropIndicator={showDropIndicator}
                              canMoveUp={index > 0}
                              canMoveDown={index < sectionColumns.length - 1}
                              onMove={moveColumn}
                              registerMoveButton={registerMoveButton}
                            />
                          </Draggable>
                        );
                      })}
                    </div>
                  </SortableContext>
                  <EndDropzone section="left" activeId={activeId} overId={overId} />
                </div>
              )}

              {/* Center (unpinned) columns */}
              {orderedColumnsPinningSections.center.length > 0 && (
                <div className="flex flex-col items-end">
                  <SortableContext items={centerSortableItems} strategy={rectSortingStrategy}>
                    <div className="flex gap-3">
                      {centerPinningSectionColumnGroups.map((group, groupIndex) => {
                        return (
                          <div key={group[0]?.id ?? groupIndex} className="relative flex-1">
                            {groupIndex > 0 && (
                              <Separator orientation="vertical" className="absolute left-[-6px] top-0 h-full" />
                            )}
                            <div className="flex flex-col">
                              {group.map((column) => {
                                const columnId = column.id || '';
                                const showDropIndicator = !!(activeId && overId === columnId);
                                const centerIndex = orderedColumnsPinningSections.center.indexOf(column);

                                return (
                                  <Draggable
                                    key={columnId}
                                    id={columnId}
                                    provideDragHandleProps
                                    disabled={!enableColumnOrdering}
                                    isDragOverlay={true}
                                    className="py-1.5"
                                  >
                                    <ColumnItem
                                      column={column}
                                      localVisibility={localVisibility}
                                      handleSwitch={handleSwitch}
                                      canHide={enableHiding}
                                      canReorder={enableColumnOrdering}
                                      showMoveButtons={showMoveButtons}
                                      isDragging={!!activeColumn}
                                      showDropIndicator={showDropIndicator}
                                      canMoveUp={centerIndex > 0}
                                      canMoveDown={centerIndex < orderedColumnsPinningSections.center.length - 1}
                                      onMove={moveColumn}
                                      registerMoveButton={registerMoveButton}
                                    />
                                  </Draggable>
                                );
                              })}
                              {groupIndex === centerPinningSectionColumnGroups.length - 1 && (
                                <EndDropzone
                                  section="center"
                                  activeId={activeId}
                                  overId={overId}
                                  className={showMoveButtons ? 'w-[264px]' : 'w-[220px]'}
                                />
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </SortableContext>
                </div>
              )}

              {/* Right pinned columns */}
              {orderedColumnsPinningSections.right.length > 0 && (
                <div className="border-grey-30 relative flex flex-col gap-2 rounded-lg border p-2">
                  <div className="flex items-center gap-1">
                    <Icon icon="drawingPin" size={16} className="text-grey-60" />
                    <Text className="text-2xs text-grey-60 font-semibold">Right Pins</Text>
                  </div>
                  <SortableContext items={rightSortableItems} strategy={verticalListSortingStrategy}>
                    <div className="flex flex-col">
                      {orderedColumnsPinningSections.right.map((column, index, sectionColumns) => {
                        const columnId = column.id || '';
                        const showDropIndicator = !!(activeId && overId === columnId);

                        return (
                          <Draggable
                            key={columnId}
                            id={columnId}
                            provideDragHandleProps
                            disabled={!enableColumnOrdering}
                            isDragOverlay={true}
                            className="py-1.5"
                          >
                            <ColumnItem
                              column={column}
                              localVisibility={localVisibility}
                              handleSwitch={handleSwitch}
                              canHide={enableHiding}
                              canReorder={enableColumnOrdering}
                              showMoveButtons={showMoveButtons}
                              showDropIndicator={showDropIndicator}
                              canMoveUp={index > 0}
                              canMoveDown={index < sectionColumns.length - 1}
                              onMove={moveColumn}
                              registerMoveButton={registerMoveButton}
                            />
                          </Draggable>
                        );
                      })}
                    </div>
                  </SortableContext>
                  <EndDropzone section="right" activeId={activeId} overId={overId} />
                </div>
              )}
            </div>
            <DragOverlay>
              {activeColumn ? (
                <div
                  inert
                  className="box-content flex h-5 items-center justify-between rounded bg-white p-1.5 shadow-lg"
                >
                  <div className="flex min-w-0 flex-1 items-center gap-1">
                    <IconButton
                      icon="dragHandleDots2"
                      aria-label={reorderLabel(columnHeaderText(activeColumn))}
                      size="icon"
                      variant="selected"
                      iconSize={16}
                    />
                    <Text className="text-primary-dark truncate text-xs">{columnHeaderText(activeColumn)}</Text>
                  </div>
                  <div className="flex items-center gap-1">
                    <Switch
                      checked={localVisibility[activeColumn.id || ''] !== false}
                      disabled={!enableHiding || activeColumn.enableHiding === false}
                    />
                  </div>
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        </DialogBody>
        <DialogFooter className="justify-between">
          <div className="flex items-center gap-2">
            {enableColumnOrdering && (
              <>
                <Switch
                  id={showMoveButtonsId}
                  checked={showMoveButtons}
                  onCheckedChange={setShowMoveButtons}
                  data-testid="manage-columns-show-reorder-buttons"
                />
                <Label htmlFor={showMoveButtonsId} className="cursor-pointer">
                  <Text className="text-primary-dark text-xs">Show reorder buttons</Text>
                </Label>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={handleCancel} data-testid="manage-columns-cancel">
              Cancel
            </Button>
            <Button onClick={handleSave} data-testid="manage-columns-apply">
              Apply
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export { ManageColumnsDialog };
